import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { API_URLS, API_CONFIG } from '../../config/api';
import { 
  Check, Clock, HelpCircle, Crown, CheckCircle, ArrowRight, ChevronDown, ChevronUp, Palette, X,
  Globe, Server, Shield, Mail, Database, Lock, Code, Zap, Cloud, Package, 
  Sparkles, Smartphone, BarChart3, Settings, Rocket, Award, Headphones, MessageCircle,
  Building, ShoppingCart, BookOpen, ExternalLink
} from 'lucide-react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../ui/accordion';
import MediaDisplay from '../MediaDisplay';

export default function WebsiteBuilderPage() {
  const [ksPackages, setKsPackages] = useState([]);
  const [ksBlogs, setKsBlogs] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [faqsLoading, setFaqsLoading] = useState(false);
  const [expandedFeatures, setExpandedFeatures] = useState({});
  const [contentReady, setContentReady] = useState(false);
  const [pageContent, setPageContent] = useState(null);

  // Fade-in animasyonu için
  useEffect(() => {
    const timer = setTimeout(() => {
      setContentReady(true);
    }, 100);
    
    return () => clearTimeout(timer);
  }, []);

  // Load page content from DB
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(`${API_URLS.ADMIN.ADMIN_API}?action=getPageContent&slug=kolay-site`);
        const txt = await res.text();
        let json; try { json = JSON.parse(txt); } catch { json = {}; }
        if (!mounted) return;
        if (json?.status === 'success' && json?.data) {
          setPageContent(json.data);
        }
      } catch (_) {}
    })();
    return () => { mounted = false; };
  }, []);

  // DB helpers
  const additional = useMemo(() => pageContent?.additional_sections || {}, [pageContent]);
  const hero = additional?.hero || {};
  const heroTitle = pageContent?.hero_title || 'Kolay Site';
  const heroBadge = pageContent?.hero_badge_text || 'Kolay Site';
  const heroDesc = pageContent?.hero_description || 'Hazır şablonlar, sürükle-bırak düzenleme ve güçlü araçlarla dakikalar içinde web sitesi oluşturun.';
  const heroImage = hero?.image_url || 'https://cdn.natrocdn.com/ResourceFiles/v3/images/webnode/webnode-img.png?v=2';
  const heroHighlight = hero?.highlight_text || 'Yapay Zeka Destekli İçerik • Hazır Tasarımlar • Ücretsiz Stok Görseller';
  const easyBuildSection = additional?.easy_build_section || {};
  const designsSection = additional?.designs_section || {};
  const designCards = Array.isArray(designsSection.cards) ? designsSection.cards : [];
  const blogSocialSection = additional?.blog_social_section || {};
  const aiStartSection = additional?.ai_start_section || {};
  const stockPhotosSection = additional?.stock_photos_section || {};
  const seoAnalyticsSection = additional?.seo_analytics_section || {};
  const ctaSection = additional?.cta_section || {};
  const valueProductsSection = additional?.value_products_section || {};
  const valueProductCards = Array.isArray(valueProductsSection.cards) ? valueProductsSection.cards : [];
  const uniqueFeaturesSection = additional?.unique_features_section || {};
  const uniqueFeatureCards = Array.isArray(uniqueFeaturesSection.cards) ? uniqueFeaturesSection.cards : [];

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await fetch(API_URLS.KOLAY_SITE_PACKAGES, { headers: { Accept: 'application/json' } });
        const text = await res.text();
        let json;
        try { json = JSON.parse(text); } catch (_) { json = { data: [] }; }
        if (!mounted) return;
        const data = Array.isArray(json?.data) ? json.data : [];
        const normalized = data.map((p) => {
          const oldPrice = p.oldPrice ?? p.old_price ?? p.original_price ?? '';
          let features = p.features;
          if (typeof features === 'string') {
            try { features = JSON.parse(features); } catch (_) { features = []; }
          }
          if (!Array.isArray(features)) features = [];
          const isActiveRaw = typeof p.is_active !== 'undefined' ? p.is_active : (p.active ?? 1);
          const is_active = typeof isActiveRaw === 'boolean' ? isActiveRaw : !!Number(isActiveRaw);
          return {
            id: p.id,
            title: p.title ?? p.name ?? '',
            oldPrice,
            price: p.price ?? '',
            features,
          is_active,
          is_popular: typeof p.is_popular !== 'undefined' ? !!p.is_popular : !!p.popular,
          sort_order: typeof p.sort_order === 'number' ? p.sort_order : parseInt(p.sort_order || 0, 10)
          };
        });
        setKsPackages(normalized.filter(p => p.is_active));
      } catch (_) {
        if (mounted) setKsPackages([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Load FAQs for Kolay Site category
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setFaqsLoading(true);
        const res = await fetch(API_URLS.FAQ);
        const json = await res.json();
        if (!mounted) return;
        const categories = json.categories || [];
        const questions = json.questions || [];
        const ksCat = categories.find(c => String(c.title || '').toLowerCase() === 'kolay site');
        if (ksCat) {
          const list = questions
            .filter(q => q.category_id === ksCat.id)
            .sort((a,b) => (a.sort_order||0)-(b.sort_order||0))
            .map(q => ({ question: q.question, answer: q.answer }));
          setFaqs(list);
        } else {
          setFaqs([]);
        }
      } catch (_) {
        if (mounted) setFaqs([]);
      } finally {
        if (mounted) setFaqsLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const fetchJson = async (url) => {
          const res = await fetch(url, { headers: { Accept: 'application/json' } });
          const raw = await res.text();
          try { return JSON.parse(raw); } catch { return { data: [] }; }
        };

        // Try server-side filtered fetch first
        let data = await fetchJson(`${API_URLS.BLOGS}&category=kolay-site&limit=3`);
        let posts = Array.isArray(data) ? data : (data.data || []);

        // Fallback: fetch all then client-side filter by slug/name if empty
        if (!Array.isArray(posts) || posts.length === 0) {
          const dataAll = await fetchJson(API_URLS.BLOGS);
          const allPosts = Array.isArray(dataAll) ? dataAll : (dataAll.blogs || dataAll.data || []);
          posts = allPosts.filter((p) => {
            const rawCat = (p.category || p.category_slug || p.category_name || '').toString().toLowerCase();
            const norm = rawCat.replace(/\s+/g, '-');
            return (rawCat.includes('kolay') && rawCat.includes('site')) || norm === 'kolay-site';
          }).slice(0, 3);
        }

        if (!mounted) return;
        const mapped = posts.map(p => ({
          id: p.id || p.slug,
          slug: p.slug || p.id,
          title: p.title,
          excerpt: p.excerpt || '',
          category: p.category_name || p.category || 'Genel',
          image: p.image || '',
          publishDate: p.published_at || p.publishDate,
          readTime: p.read_time || '5 dakika'
        }));
        setKsBlogs(mapped.slice(0, 3));
      } catch (_) {
        if (mounted) setKsBlogs([]);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const normalizeImageUrl = (url) => {
    if (!url) return '';
    const BASE_URL = API_CONFIG.BASE_URL;
    try {
      const u = String(url);
      if (u.includes('getScreenshot') && u.includes('file=')) {
        const match = u.match(/file=([^&]+)/);
        if (match && match[1]) return `${BASE_URL}/uploads/${decodeURIComponent(match[1])}`;
      }
      if (u.startsWith('http://') || u.startsWith('https://')) return u;
      if (u.startsWith('/backend/uploads/')) return `${BASE_URL}${u.replace('/backend', '')}`;
      if (u.startsWith('/uploads/')) return `${BASE_URL}${u}`;
      if (u.startsWith('/blog/')) return `${BASE_URL}/uploads/${u.split('/').pop()}`;
      if (!u.startsWith('/')) return `${BASE_URL}/uploads/${u}`;
      return `${BASE_URL}${u}`;
    } catch { return url; }
  };

  const toggleFeatures = (planId) => {
    // Synchronized: Bir pakette açılırsa hepsinde aç/kapa
    const currentState = expandedFeatures[planId];
    const newState = !currentState;
    
    // Tüm paketler için aynı state'i set et
    const newExpandedState = {};
    displayPackages.forEach(pkg => {
      newExpandedState[pkg.id] = newState;
    });
    
    setExpandedFeatures(newExpandedState);
  };

  const displayPackages = ksPackages;

  // Paket kartı bileşeni - Hosting sayfasındaki ile aynı
  const PackageCard = ({ plan, index }) => {
    const isExpanded = expandedFeatures[plan.id || index];
    
    // Özellikleri normalize et
    const normalizedFeatures = (plan.features || []).map(f => {
      if (typeof f === 'string') {
        return { name: f, included: true };
      } else if (f && typeof f === 'object') {
        return { 
          name: f.name ?? JSON.stringify(f), 
          included: f.included !== false 
        };
      }
      return { name: String(f), included: true };
    });

    return (
      <Card
        className={`relative border-0 shadow-xl flex flex-col h-full ${
          plan.is_popular ? 'ring-4 ring-purple-200' : ''
        } bg-white`}
      >
        {/* Popular Badge */}
        {plan.is_popular && (
          <div className="absolute top-0 transform -translate-x-1/2 -translate-y-1/2 z-10" style={{ left: '52%' }}>
            <Badge className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-4 py-1 text-sm font-bold" style={{ width: '150px' }}>
              <Crown className="w-4 h-4 mr-1" />
              EN POPÜLER
            </Badge>
          </div>
        )}

        <CardHeader className="space-y-4 pb-6 flex-shrink-0">
          <div className="space-y-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-blue-600 to-purple-600 flex items-center justify-center">
              <Palette className="w-8 h-8 text-white" />
            </div>
            
            <div className="space-y-2">
              <CardTitle className="text-2xl font-bold text-gray-900">
                {plan.title}
              </CardTitle>
              <p className="text-gray-600 leading-relaxed">Kolay Site ile hızlıca web sitenizi oluşturun</p>
            </div>
          </div>

          {/* Pricing */}
          <div className="space-y-2">
            <div className="flex items-baseline space-x-2">
              <span className="text-4xl font-bold text-gray-900">
                {plan.price}
              </span>
              <span className="text-sm text-gray-600">
                /yıllık
              </span>
            </div>
            
            {plan.oldPrice && (
              <div className="flex items-center space-x-2">
                <span className="text-lg text-gray-400 line-through">
                  {plan.oldPrice}
                </span>
              </div>
            )}
          </div>
        </CardHeader>

        <CardContent className="flex flex-col flex-1">
          {/* Features - flex-grow ile genişler */}
          <div className="space-y-3 flex-grow">
            <h4 className="font-semibold text-gray-800">Özellikler:</h4>
            {normalizedFeatures.slice(0, 5).map((feature, idx) => {
              const isIncluded = feature.included !== false;
              return (
                <div 
                  key={idx} 
                  className="flex items-center space-x-3"
                >
                  {isIncluded ? (
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                  ) : (
                    <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                  <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                    {feature.name}
                  </span>
                </div>
              );
            })}
            
            {/* Ek Özellikler */}
            {normalizedFeatures.length > 5 && (
              <div>
                <button
                  onClick={() => toggleFeatures(plan.id)}
                  className="flex items-center justify-center w-full text-sm text-blue-600 hover:text-blue-800 transition-colors mt-4"
                >
                  {isExpanded ? (
                    <>
                      <ChevronUp className="w-4 h-4 mr-1" />
                      Daha Az Detay
                    </>
                  ) : (
                    <>
                      <ChevronDown className="w-4 h-4 mr-1" />
                      Daha Fazla Detay
                    </>
                  )}
                </button>

                {isExpanded && (
                  <div className="space-y-3 mt-4 pt-4 border-t border-gray-200">
                    {normalizedFeatures.slice(5).map((feature, idx) => {
                      const isIncluded = feature.included !== false;
                      return (
                        <div 
                          key={idx} 
                          className="flex items-center space-x-3"
                        >
                          {isIncluded ? (
                            <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                          ) : (
                            <X className="w-5 h-5 text-gray-400 flex-shrink-0" />
                          )}
                          <span className={`text-sm font-medium ${isIncluded ? 'text-gray-900' : 'text-gray-400 line-through'}`}>
                            {feature.name}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* CTA Button - Always at bottom */}
          <div className="mt-6 flex-shrink-0">
            <Button 
              className="w-full h-12 font-semibold bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
            >
              <span>PLANI SEÇ</span>
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="">
      {/* Hero */}
      <section 
        className="bg-gradient-to-r from-blue-600 via-purple-600 to-blue-800 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
            <div className="text-center lg:text-left">
              <div>
                <p className="text-orange-300 font-semibold">{heroBadge}</p>
                <h1 className="text-3xl md:text-5xl font-extrabold leading-tight mt-2">
                  {heroTitle}
                </h1>
              </div>
              <p className="text-blue-100 mt-4 text-lg">{heroDesc}</p>
              <ul className="mt-6 space-y-3 text-blue-100">
                <li className="flex items-center gap-2 justify-center lg:justify-start"><span className="w-2 h-2 bg-yellow-300 rounded-full"></span> {heroHighlight}</li>
              </ul>
              <div className="mt-8 flex gap-3 justify-center lg:justify-start">
                <Link to="/web-sitesi-tasarimlari?type=easy" className="inline-flex items-center h-11 px-6 rounded-lg bg-white text-blue-700 hover:bg-gray-100 font-medium">
                  Tasarımları İncele
                </Link>
                <Link to="/iletisim" className="inline-flex items-center h-11 px-6 rounded-lg border border-white/40 text-white hover:bg-white/10 font-medium">
                  İletişime Geç
                </Link>
              </div>
            </div>
            <div className="hidden lg:block">
              <MediaDisplay
                src={heroImage}
                alt="Kolay Site Hero"
                className="w-full rounded-2xl border border-white/20"
                videoProps={{ controls: true }}
              />
            </div>
          </div>
        </div>
      </section>

      

      {/* Paketler (Hero sonrası) */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-gray-900">Paketler</h2>
            <p className="text-gray-600 mt-3">İhtiyacınıza uygun Kolay Site paketini seçin, hemen yayınlayın.</p>
          </div>
          <>
            <style>{`
              .kolay-site-packages {
                display: grid;
                gap: 2rem;
                max-width: 72rem;
                margin: 0 auto;
                grid-template-columns: 1fr;
              }
              @media (min-width: 768px) {
                .kolay-site-packages {
                  grid-template-columns: repeat(2, minmax(0, 1fr));
                }
              }
              @media (min-width: 1024px) {
                .kolay-site-packages {
                  grid-template-columns: repeat(${Math.min(displayPackages.length, 4)}, minmax(0, 1fr));
                }
              }
            `}</style>
            <div className="kolay-site-packages">
            {displayPackages
              .slice()
              .sort((a,b) => (a.sort_order||0) - (b.sort_order||0))
              .map((p, idx) => (
                <PackageCard key={p.id || idx} plan={p} index={idx} />
              ))}
            </div>
          </>
        </div>
      </section>

      

      {/* Yapay Zeka Desteği ile Web Sitesi Oluşturmak Çok Kolay */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div className="text-center md:text-left">
              <div className="text-3xl font-bold text-gray-900">{easyBuildSection.title || 'Kolay Site ile Web Sitesi Oluşturmak Çok Kolay'}</div>
              <div className="mt-4 space-y-4 text-gray-600 text-lg">
                <p>{easyBuildSection.description || ''}</p>
              </div>
            </div>
            <MediaDisplay
              className="w-full rounded-xl"
              src="https://cdn.natrocdn.com/ResourceFiles/v3/images/webnode/webnode-img.png?v=2"
              alt="Yapay Zeka Desteği"
              videoProps={{ controls: true }}
            />
          </div>
        </div>
      </section>

      {/* Yüzlerce Profesyonel Web Tasarımı */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-10">
            <h2 className="text-3xl md:text-4xl font-extrabold text-gray-900">{designsSection.title || 'Yüzlerce Profesyonel Web Tasarımı'}</h2>
            <p className="mt-4 text-gray-600">{designsSection.subtitle || 'Sektörünüze uygun hazır şablonlar'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {designCards.length > 0 ? designCards.map((c, i) => {
              // İkon belirleme - önce DB'den, yoksa title'a göre
              const iconMap = { Building, ShoppingCart, BookOpen, Globe, Code, Palette, Sparkles };
              let IconComponent = iconMap[c.icon] || Building;
              let colorClass = c.color || 'from-blue-500 to-cyan-500';
              
              // Fallback: title'a göre belirle
              if (!c.icon) {
                const title = (c.title || '').toLowerCase();
                if (title.includes('kurumsal') || title.includes('corporate')) {
                  IconComponent = Building;
                  colorClass = 'from-blue-500 to-cyan-500';
                } else if (title.includes('e-ticaret') || title.includes('ecommerce')) {
                  IconComponent = ShoppingCart;
                  colorClass = 'from-green-500 to-emerald-500';
                } else if (title.includes('blog') || title.includes('portfolio')) {
                  IconComponent = BookOpen;
                  colorClass = 'from-purple-500 to-pink-500';
                }
              }
              
              return (
                <div key={i} className="border rounded-2xl p-6 bg-white hover:shadow-lg transition-shadow group">
                  <div className="flex items-center justify-center mb-4">
                    <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${colorClass} flex items-center justify-center transform group-hover:scale-110 transition-transform`}>
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-gray-900 text-center">{c.title || ''}</div>
                  <div className="text-sm text-gray-600 mt-2 text-center">{c.description || ''}</div>
                </div>
              );
            }) : (
              <>
                <div className="border rounded-2xl p-6 bg-white hover:shadow-lg transition-shadow group">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <Building className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-gray-900 text-center">Kurumsal</div>
                  <div className="text-sm text-gray-600 mt-2 text-center">Profesyonel kurumsal web sitesi tasarımları</div>
                </div>
                <div className="border rounded-2xl p-6 bg-white hover:shadow-lg transition-shadow group">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-green-500 to-emerald-500 flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <ShoppingCart className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-gray-900 text-center">E-Ticaret</div>
                  <div className="text-sm text-gray-600 mt-2 text-center">Online satış için optimize edilmiş tasarımlar</div>
                </div>
                <div className="border rounded-2xl p-6 bg-white hover:shadow-lg transition-shadow group">
                  <div className="flex items-center justify-center mb-4">
                    <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center transform group-hover:scale-110 transition-transform">
                      <BookOpen className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="text-lg font-semibold text-gray-900 text-center">Blog/Portfolio</div>
                  <div className="text-sm text-gray-600 mt-2 text-center">Kişisel ve blog siteleri için modern tasarımlar</div>
                </div>
              </>
            )}
          </div>
          <div className="text-center mt-10">
            <Link to="/web-sitesi-tasarimlari?type=easy" className="inline-flex items-center h-11 px-6 rounded-lg bg-blue-600 text-white hover:bg-blue-700 font-medium">
              Tüm Tasarımları Göster
            </Link>
          </div>
        </div>
      </section>

      {/* Blog Sayfası Oluşturma ve Sosyal Medya Desteği */}
      <section 
        className="py-16 bg-gradient-to-b from-white to-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <div>
              <MediaDisplay
                src="https://cdn.natrocdn.com/ResourceFiles/v3/images/webnode/blog-image.png"
                alt="Blog Sayfası Oluşturma"
                className="w-full rounded-xl"
                videoProps={{ controls: true }}
              />
            </div>
            <div className="text-center md:text-left">
              <h2 className="text-3xl font-bold text-gray-900">{blogSocialSection.title || 'Blog Sayfası Oluşturma ve Sosyal Medya Desteği'}</h2>
              <div className="mt-4 space-y-4 text-gray-700 text-lg">
                <p>{blogSocialSection.description || ''}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Support Section */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col-reverse md:grid md:grid-cols-2 gap-10 items-center">
            <div className="text-center md:text-left">
              <div className="text-3xl font-bold text-gray-900">{aiStartSection.title || 'Yapay Zeka Desteği ile Hızlı Başlangıç'}</div>
              <div className="mt-4 space-y-4 text-gray-600 text-lg">
                <p>{aiStartSection.description || ''}</p>
              </div>
            </div>
            <MediaDisplay
              className="w-full rounded-xl"
              src="https://cdn.natrocdn.com/ResourceFiles/v3/images/webnode/webnode-img.png?v=2"
              alt="Yapay Zeka Desteği"
              videoProps={{ controls: true }}
            />
          </div>
        </div>
      </section>

      {/* Stock Images Section */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
            <MediaDisplay
              className="w-full rounded-xl"
              src="https://cdn.natrocdn.com/ResourceFiles/v3/images/webnode/stock-images.png"
              alt="Ücretsiz Stok Görseller"
              videoProps={{ controls: true }}
            />
            <div className="text-center md:text-left">
              <div className="text-3xl font-bold text-gray-900">{stockPhotosSection.title || 'Ücretsiz Stok Görsel Kütüphanesi'}</div>
              <p className="mt-4 text-gray-600 text-lg">{stockPhotosSection.description || ''}</p>
            </div>
          </div>
        </div>
      </section>

      {/* SEO + Analytics Section */}
      <section 
        className="py-16"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col-reverse md:grid md:grid-cols-2 gap-10 items-center">
            <div className="text-center md:text-left">
          <div className="text-3xl font-bold text-gray-900">{seoAnalyticsSection.title || 'SEO Araçları ve Analitik'}</div>
              <div>
            <p className="mt-4 text-gray-600 text-lg">{seoAnalyticsSection.description || 'Sayfa başlığı ve açıklamaları, özel URL yapıları ve Google Analytics/GTM entegrasyonları ile arama motorlarında üst sıraları hedefleyin.'}</p>
                <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 text-gray-700 justify-center md:justify-start">
                  <li className="flex items-center gap-2 justify-center md:justify-start"><span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span> SEO başlık ve açıklama alanları</li>
                  <li className="flex items-center gap-2 justify-center md:justify-start"><span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span> Google Analytics ve GTM</li>
                  <li className="flex items-center gap-2 justify-center md:justify-start"><span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span> Mobil uyumlu ve hızlı altyapı</li>
                  <li className="flex items-center gap-2 justify-center md:justify-start"><span className="w-1.5 h-1.5 bg-purple-600 rounded-full"></span> Ücretsiz SSL desteği</li>
                </ul>
              </div>
            </div>
            <MediaDisplay
              className="w-full rounded-xl"
              src="https://cdn.natrocdn.com/ResourceFiles/v3/images/webnode/seo-image.png?v=3"
              alt="SEO ve Analitik"
              videoProps={{ controls: true }}
            />
          </div>
        </div>
      </section>

      {/* (Kaldırıldı) İkinci Paketler bölümü, yinelenmeyi önlemek için kaldırıldı */}

      {/* Görsel CTA alanı */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative rounded-2xl overflow-hidden">
            <MediaDisplay src="https://cdn.natrocdn.com/ResourceFiles/v3/images/natrosite/icons/woman-img.png" alt="cta" className="w-full hidden md:block" videoProps={{ controls: true }} />
            <div className="md:absolute inset-x-0 md:top-[26%] mx-auto bg-white md:bg-white/90 rounded-xl p-6 max-w-xl text-center">
              <h3 className="text-2xl font-bold text-gray-900">Başlamaya Hazır mısınız?</h3>
              <p className="text-gray-700 mt-2">İhtiyacınıza uygun hazır site tasarımını seçin, içerikleri düzenleyin ve yayına alın.</p>
              <div className="mt-4">
                <Link to="/web-sitesi-tasarimlari?type=easy" className="inline-flex items-center h-11 px-6 rounded-lg bg-purple-600 text-white hover:bg-purple-700 font-medium">Kolay Site ile Başla</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Blog Teasers (Kolay Site kategorisi) */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-gray-900">Kolay Site Rehberi</h2>
            <p className="text-gray-600 mt-3">Hızlı ve profesyonel web sitesi oluşturmanız için ipuçları, öneriler ve güncel içerikler.</p>
          </div>
          <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
            {(ksBlogs.length ? ksBlogs : []).map((b) => {
              const hasValidImage = b.image && !b.image.includes('placeholder');
              
              return (
                <Card key={b.id} className="overflow-hidden border-purple-100 hover:shadow-lg transition-shadow">
                  {hasValidImage ? (
                    <div className="aspect-[16/9] bg-purple-50 overflow-hidden">
                      <MediaDisplay
                        src={normalizeImageUrl(b.image)}
                        alt={b.title}
                        className="w-full h-full object-cover transition-transform duration-300 hover:scale-105"
                        videoProps={{ controls: true }}
                        imgProps={{
                          loading: "lazy",
                          onError: (e) => {
                            e.target.style.display = 'none';
                            e.target.parentElement.innerHTML = '<div class="w-full h-full bg-gradient-to-br from-purple-500 to-pink-700 flex items-center justify-center"><svg class="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg></div>';
                          }
                        }}
                      />
                    </div>
                  ) : (
                    <div className="aspect-[16/9] bg-gradient-to-br from-purple-500 to-pink-700 flex items-center justify-center">
                      <svg className="w-16 h-16 text-white/50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
                      </svg>
                    </div>
                  )}
                  <CardContent className="p-5 space-y-3">
                    <div className="inline-flex items-center px-3 py-1 bg-purple-50 text-purple-600 text-xs font-semibold rounded-full">
                      <BookOpen className="w-3.5 h-3.5 mr-1" />
                      Kolay Site
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900">{b.title}</h3>
                    <p className="text-sm text-gray-600 leading-relaxed">{b.excerpt || ''}</p>
                    <button
                      onClick={() => window.location.href = `/blog/${b.slug || b.id}`}
                      className="inline-flex items-center text-sm font-semibold text-purple-600 hover:text-purple-700"
                    >
                      Yazıyı Oku
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </button>
                  </CardContent>
                </Card>
              );
            })}
            {ksBlogs.length === 0 && (
              <div className="col-span-1 md:col-span-3 text-center text-gray-500">Kolay Site kategorisinde içerik bulunamadı.</div>
            )}
          </div>
        </div>
      </section>

      {/* Web Sitenize Değer Katacak Ürünler (ikonlarla) */}
      <section 
        className="py-16 bg-[#172a40]"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-white">{valueProductsSection.title || 'Web Sitenize Değer Katacak Ürünler'}</h2>
            <p className="text-white/80 mt-3">{valueProductsSection.subtitle || 'Web sitesi oluşturma ve yönetim ile ilgili faydalı ürünler.'}</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {(valueProductCards.length ? valueProductCards : []).map((p, i) => {
              // İkon belirleme - önce DB'den, yoksa title'a göre
              const iconMap = { Globe, Server, Shield, Mail, Database, Lock, Code, Zap, Cloud, Package };
              let IconComponent = iconMap[p.icon] || Globe;
              
              // Fallback: icon field yoksa title'a göre belirle
              if (!p.icon) {
                const title = (p.title || '').toLowerCase();
                if (title.includes('hosting') || title.includes('barındırma')) IconComponent = Server;
                else if (title.includes('domain') || title.includes('alan')) IconComponent = Globe;
                else if (title.includes('ssl') || title.includes('güvenlik')) IconComponent = Shield;
                else if (title.includes('mail') || title.includes('e-posta')) IconComponent = Mail;
                else if (title.includes('veritabanı') || title.includes('database')) IconComponent = Database;
                else if (title.includes('yedek') || title.includes('backup')) IconComponent = Cloud;
              }
              
              return (
                <a key={i} href={p.link || '#'} className="rounded-xl bg-white block p-8 text-center border border-gray-200 hover:shadow-lg transition-all duration-300 hover:border-purple-400">
                  <div className="flex justify-center mb-4">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-blue-500">
                      <IconComponent className="w-7 h-7 text-white" />
                    </div>
                  </div>
                  <div className="text-xl font-semibold text-gray-900">{p.title || ''}</div>
                  <p className="text-gray-700 mt-2">{p.description || p.desc || ''}</p>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Kolay Site'nin Benzersiz Özellikleri */}
      <section 
        className="py-16 bg-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-10">
            <h2 className="text-3xl font-bold text-gray-900">{uniqueFeaturesSection.title || "Kolay Site'nin Benzersiz Özellikleri"}</h2>
            <p className="text-gray-600 mt-3"></p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {(uniqueFeatureCards.length ? uniqueFeatureCards : []).map((f, i) => {
              // İkon belirleme - önce DB'den, yoksa title'a göre
              const iconMap = { 
                Sparkles, Palette, Code, Zap, Globe, Smartphone, 
                BarChart3, Settings, Shield, Rocket, Clock, Award, Headphones
              };
              let IconComponent = iconMap[f.icon] || Sparkles;
              
              // Fallback: icon field yoksa title'a göre belirle
              if (!f.icon) {
                const title = (f.title || '').toLowerCase();
                if (title.includes('tasarım') || title.includes('şablon')) IconComponent = Palette;
                else if (title.includes('hız') || title.includes('performans')) IconComponent = Zap;
                else if (title.includes('mobil') || title.includes('responsive')) IconComponent = Smartphone;
                else if (title.includes('seo') || title.includes('arama')) IconComponent = BarChart3;
                else if (title.includes('kod') || title.includes('özelleştir')) IconComponent = Code;
                else if (title.includes('güvenli') || title.includes('ssl')) IconComponent = Shield;
                else if (title.includes('analiz') || title.includes('analitik')) IconComponent = BarChart3;
                else if (title.includes('destek') || title.includes('yardım')) IconComponent = Headphones;
              }
              
              return (
                <div key={i} className="text-center group">
                  <div className="mb-4 flex justify-center">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="h-8 w-8 text-white" />
                    </div>
                  </div>
                  <div className="text-xl font-semibold text-gray-900">{f.title || ''}</div>
                  <p className="text-gray-700 mt-2">{f.description || f.desc || ''}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section 
        className="py-16 bg-gray-50"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Sıkça Sorulan <span className="text-purple-600">Sorular</span>
            </h2>
            <p className="text-gray-600 text-lg max-w-3xl mx-auto">
              Kolay Site hakkında merak ettiğiniz her şeyi burada bulabilirsiniz
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            {faqsLoading ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Yükleniyor...</p>
              </div>
            ) : faqs.length === 0 ? (
              <div className="text-center p-8 bg-white rounded-xl">
                <p className="text-gray-500">Bu kategori için soru bulunamadı.</p>
              </div>
            ) : (
              <Accordion type="single" collapsible className="w-full">
                {faqs.map((f, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left">
                      <div className="flex items-center">
                        <HelpCircle className="w-5 h-5 mr-3 text-purple-600" />
                        {f.question}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="text-gray-600">
                      {f.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section 
        className="py-20 bg-gradient-to-r from-purple-600 to-pink-600 text-white"
        style={{ 
          opacity: contentReady ? 1 : 0, 
          transition: 'opacity 0.6s ease-in' 
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h2 className="text-4xl font-bold mb-4">
              {ctaSection.title || 'Kolay Site ile Hemen Başlayın!'}
            </h2>
            <p className="text-xl text-purple-100 mb-8 max-w-3xl mx-auto">
              {ctaSection.description || 'Profesyonel web sitesi tasarımlarımız hakkında detaylı bilgi almak ve size özel paket önerileri için ekibimizle iletişime geçin.'}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <Link
                to="/iletisim"
                className="inline-flex items-center px-8 py-4 bg-white text-purple-600 rounded-lg hover:bg-gray-100 font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-lg"
              >
                <Mail className="w-5 h-5 mr-2" />
                İletişime Geç
              </Link>
              <a
                href={`https://api.whatsapp.com/send/?phone=905324404011&text=${encodeURIComponent('Merhaba, Kolay Site hizmeti hakkında bilgi almak istiyorum.')}`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center px-8 py-4 bg-pink-700 text-white rounded-lg hover:bg-pink-800 font-semibold text-lg transition-all duration-300 transform hover:scale-105 border-2 border-white shadow-lg"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                WhatsApp İletişim
              </a>
            </div>
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-8">
              {(ctaSection.highlights && Array.isArray(ctaSection.highlights) ? ctaSection.highlights : [
                { icon: 'Clock', title: 'Hızlı Kurulum', description: 'Dakikalar içinde web sitenizi yayına alın' },
                { icon: 'Palette', title: 'Profesyonel Tasarımlar', description: 'Yüzlerce hazır şablon arasından seçin' },
                { icon: 'Headphones', title: '7/24 Destek', description: 'Uzman ekibimiz her zaman yanınızda' }
              ]).map((highlight, idx) => {
                const iconMap = { Clock, Palette, Headphones, Shield, Award, Zap, Sparkles, Globe };
                const HighlightIcon = iconMap[highlight.icon] || Clock;
                
                return (
                  <div key={idx} className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
                    <HighlightIcon className="w-12 h-12 mx-auto mb-4" />
                    <h3 className="font-semibold text-lg mb-2">{highlight.title}</h3>
                    <p className="text-purple-100">{highlight.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}


